import { Box, Button, Grid, TextField } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import jwt from 'jsonwebtoken';
import React, { useState } from 'react';

import Spinner from './Spinner';
import Title from './Title';

import { useDispatch, useSelector } from 'react-redux';
import {
    _did,
    _didDoc,
    _privateKey,
    _publicKey,
    setDID,
    setDIDDocument,
    setPrivateKey,
    setPublicKey
} from '../redux/offUpdaterSlice';

import axios from 'axios';
import { utils as sdVcUtils } from 'sd-vc-lib';
import { WizardButton } from './ui/WizardButton';
import { Sync } from '@material-ui/icons';
import JsonViewer from './ui/JsonViwer';

const Ed25519 = sdVcUtils.ed25519;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            '& > * + *': {
                marginTop: theme.spacing(2)
            }
        },
        btn: {
            marginBottom: 10
        }
    })
);

export default function OCDIDUpdater() {
    const did = useSelector(_did);
    const privateKey = useSelector(_privateKey);
    const publicKey = useSelector(_publicKey);
    const didDoc = useSelector(_didDoc);
    const [isValidDID, setIsValidDID] = useState(true);
    const [isUpdated, setIsUpdated] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    const dispatch = useDispatch();

    const classes = useStyles();

    async function handleUpdateDID() {
        const ed = new Ed25519();
        setIsUpdating(true);

        const challengeResponse = await axios.patch(
            `${process.env.REACT_APP_DID_RESOLVER_URL}/challenge`
        );
        const { challenge: challenge } = jwt.decode(challengeResponse.data.challengeToken) as any;

        axios
            .patch(
                `${process.env.REACT_APP_DID_RESOLVER_URL}/did/${did}`,
                {
                    publicKey: publicKey,
                    cipherText: ed
                        .sign(
                            Buffer.from(challenge, 'hex'),
                            Buffer.from(privateKey as string, 'hex')
                        )
                        .toHex(),
                    authentication: {
                        type: 'Ed25519VerificationKey2018',
                        publicKeyBase58: publicKey
                    }
                },
                {
                    headers: {
                        Authorization: challengeResponse.data.challengeToken
                    }
                }
            )
            .then((res) => {
                setIsValidDID(true);

                dispatch(
                    setDIDDocument(JSON.stringify(res.data.newResolution.didDocument, null, 4))
                );
                setIsUpdated(true);
            })
            .catch((err) => {
                console.log('err', err.response.data.error);
                setIsUpdated(false);
                if (err.response && err.response.data.error) {
                    dispatch(setDIDDocument(err.response.data.error));
                } else {
                    dispatch(setDIDDocument('Error'));
                }
            })
            .finally(() => {
                setIsUpdating(false);
            });
    }

    function handleDidInput(did: string): void {
        dispatch(setDID(did));
    }

    function handlePrivateKeyInput(privateKey: string): void {
        dispatch(setPrivateKey(privateKey));
    }

    function handlePublicKeyInput(publicKey: string): void {
        dispatch(setPublicKey(publicKey));
    }

    return (
        <div>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box
                        display="flex"
                        justifyContent="flex-start"
                        alignItems="center"
                        style={{ gap: 16 }}>
                        <Box
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 10,
                                width: 38,
                                height: 38,
                                backgroundColor: '#2ec4b6',
                                color: '#fff'
                            }}>
                            <Sync style={{ color: '#ffffff' }} />
                        </Box>
                        <Title>{'Update Offchain DID'}</Title>
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    <div
                        style={{
                            border: '1px solid #2ec4b6',
                            borderRadius: 12,
                            padding: 16,
                            backgroundColor: '#fff'
                        }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    id="did"
                                    label="DID"
                                    placeholder="did:key:0000000000000000000000000000000"
                                    variant="outlined"
                                    fullWidth
                                    value={did}
                                    onChange={(e: any) => handleDidInput(e.target.value)}
                                    error={!isValidDID}
                                    helperText={isValidDID ? '' : 'Invalid DID'}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <div className={classes.root}>
                                    <Alert severity="info">
                                        Your private key will never go outside this machine. Private
                                        key will only be used for verify the ownership.
                                    </Alert>
                                </div>
                                <TextField
                                    id="privateKey"
                                    label="Private Key of DID controller"
                                    variant="outlined"
                                    fullWidth
                                    value={privateKey}
                                    style={{ marginTop: 10 }}
                                    onChange={(e: any) => handlePrivateKeyInput(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    id="publicKey"
                                    label="Public Key"
                                    variant="outlined"
                                    fullWidth
                                    value={publicKey}
                                    onChange={(e: any) => handlePublicKeyInput(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <WizardButton
                                    className={classes.btn}
                                    onClick={handleUpdateDID}
                                    variant="primary"
                                    style={{ textTransform: 'uppercase' }}
                                    endIcon={<Sync fontSize="large" />}>
                                    Update DID
                                </WizardButton>
                            </Grid>

                            <Grid item xs={12}>
                                {didDoc && (
                                    <Box>
                                        <JsonViewer content={didDoc} header="DID Document" />
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </div>
                </Grid>
            </Grid>
            {isUpdating && <Spinner />}
        </div>
    );
}
